import React from 'react';
import { ClipboardList, Flame, CheckCircle2, Truck, MapPin, Home, Check, Clock, XCircle } from 'lucide-react';
import { formatIST } from '../utils/dateUtils';

const steps = [
    { 
        id: 'placed', 
        label: 'Placed', 
        icon: ClipboardList,
        isCompleted: (order) => ['confirmed', 'preparing', 'ready', 'delivered'].includes(order.status),
        isActive: (order) => order.status === 'confirmed' || order.status === 'pending'
    },
    { 
        id: 'preparing', 
        label: 'Preparing', 
        icon: Flame,
        isCompleted: (order) => ['ready', 'delivered'].includes(order.status),
        isActive: (order) => order.status === 'preparing'
    },
    { 
        id: 'ready', 
        label: 'Ready', 
        icon: CheckCircle2,
        isCompleted: (order) => ['delivered'].includes(order.status) || (order.delivery_status && order.delivery_status !== 'pending' && order.delivery_status !== 'unassigned'),
        isActive: (order) => order.status === 'ready' && (!order.delivery_status || order.delivery_status === 'pending' || order.delivery_status === 'unassigned')
    },
    { 
        id: 'picked', 
        label: 'Picked', 
        icon: Truck,
        isCompleted: (order) => ['in_transit', 'delivered'].includes(order.delivery_status),
        isActive: (order) => order.delivery_status === 'picked_up'
    },
    { 
        id: 'on_way', 
        label: 'On Way', 
        icon: MapPin,
        isCompleted: (order) => order.delivery_status === 'delivered' || order.status === 'delivered',
        isActive: (order) => order.delivery_status === 'in_transit'
    },
    { 
        id: 'delivered', 
        label: 'Delivered', 
        icon: Home,
        isCompleted: (order) => order.status === 'delivered',
        isActive: (order) => order.status === 'delivered'
    }
];

export default function OrderStatusTracker({ order }) {
    if (order.status === 'cancelled') {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                        <h3 className="font-bold text-red-800">Order Cancelled</h3>
                        {order.cancelled_at && (
                            <p className="text-xs text-red-500">
                                {new Date(order.cancelled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                            </p>
                        )}
                    </div>
                </div>
                {order.cancellation_reason && (
                    <p className="text-sm text-red-700 mt-2">
                        <span className="font-medium">Reason:</span> {order.cancellation_reason}
                    </p>
                )}
                {order.refund_status && order.refund_status !== 'none' && (
                    <div className={`mt-3 text-xs font-bold px-3 py-1.5 rounded-full w-fit ${
                        order.refund_status === 'initiated' ? 'bg-yellow-100 text-yellow-800' :
                        order.refund_status === 'processed' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                        Refund {order.refund_status.charAt(0).toUpperCase() + order.refund_status.slice(1)}
                        {order.refund_amount ? ` • ₹${parseFloat(order.refund_amount).toFixed(2)}` : ''}
                    </div>
                )}
            </div>
        );
    }

    // ETA Calculation
    const prepTimeMinutes = 20;
    const deliveryTimePerKm = 5;
    const distance = parseFloat(order.delivery_distance_km) || 0;
    const totalMinutes = prepTimeMinutes + (distance * deliveryTimePerKm);
    
    const createdAt = new Date(order.created_at);
    const etaDate = new Date(createdAt.getTime() + totalMinutes * 60000);
    const etaTime = formatIST(etaDate);

    // Determine current index to animate the line
    const currentStepIndex = steps.findIndex(step => step.isActive(order));
    const lastCompletedIndex = steps.reduce((acc, step, index) => step.isCompleted(order) ? index : acc, -1);
    
    // Total progress for the line (0 to 100)
    const progress = lastCompletedIndex === -1 ? 0 : ((lastCompletedIndex + 1) / (steps.length - 1)) * 100;

    return (
        <div className="w-full py-6 px-2">
            {/* ETA Header */}
            <div className="mb-8 flex flex-col items-center">
                <div className="bg-primary-50 px-4 py-2 rounded-full border border-primary-100 flex items-center space-x-2 animate-pulse-slow">
                    <Clock className="w-4 h-4 text-primary-600" />
                    <span className="text-sm font-bold text-primary-900">
                        {order.status === 'delivered' ? 'Delivered At' : 'Estimated Arrival'}: {order.status === 'delivered' ? formatIST(order.updated_at) : etaTime}
                    </span>
                </div>
                {distance > 0 && (
                    <p className="mt-2 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                        Distance: {distance.toFixed(1)} km • Est. {Math.round(totalMinutes)} mins
                    </p>
                )}
            </div>

            <div className="relative">
                {/* Progress Bar Background */}
                <div className="absolute top-1/2 left-0 w-full h-1.5 bg-gray-100 -translate-y-1/2 rounded-full overflow-hidden">
                    {/* Completed Progress Line */}
                    <div 
                        className="h-full bg-primary-500 transition-all duration-1000 ease-out relative"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                    >
                        {/* Shimmer effect for completed line */}
                        <div className="absolute inset-0 bg-white/20 animate-shimmer" style={{ backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)' }}></div>
                    </div>
                    
                    {/* Moving Active Line (Next Segment) */}
                    {currentStepIndex !== -1 && currentStepIndex < steps.length - 1 && (
                        <div 
                            className="absolute top-0 h-full bg-primary-300/30 animate-flow-line"
                            style={{ 
                                left: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
                                width: `${(1 / (steps.length - 1)) * 100}%`,
                            }}
                        ></div>
                    )}
                </div>

                {/* Steps */}
                <div className="relative flex justify-between">
                    {steps.map((step, index) => {
                        const Icon = step.icon;
                        const isCompleted = step.isCompleted(order);
                        const isActive = step.isActive(order);
                        
                        return (
                            <div key={step.id} className="flex flex-col items-center">
                                {/* Icon Container */}
                                <div 
                                    className={`
                                        relative z-10 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500
                                        ${isCompleted ? 'bg-primary-600 text-white shadow-lg shadow-primary-200' : 
                                          isActive ? 'bg-white border-2 border-primary-500 text-primary-600 shadow-xl scale-125' : 
                                          'bg-white border-2 border-gray-200 text-gray-400'}
                                    `}
                                >
                                    {isCompleted ? (
                                        <Check className="w-5 h-5 animate-in zoom-in duration-300" />
                                    ) : (
                                        <Icon className={`w-5 h-5 ${isActive ? 'animate-bounce-subtle' : ''}`} />
                                    )}

                                    {/* Active Pulse Effect */}
                                    {isActive && (
                                        <div className="absolute inset-0 rounded-full bg-primary-400 animate-ping opacity-25"></div>
                                    )}
                                </div>

                                {/* Label */}
                                <span className={`
                                    mt-4 text-[9px] md:text-[11px] font-black uppercase tracking-tighter transition-colors duration-300
                                    ${isCompleted || isActive ? 'text-gray-900' : 'text-gray-400'}
                                `}>
                                    {step.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            {/* Status Message */}
            <div className="mt-10 text-center bg-gray-50/80 py-4 px-6 rounded-2xl border border-gray-100 shadow-sm">
                <p className="text-sm font-semibold text-gray-800 leading-relaxed">
                    {order.status === 'confirmed' && "🚀 We've received your order! Preparing soon."}
                    {order.status === 'preparing' && "👨‍🍳 Our chef is crafting your delicious meal!"}
                    {order.status === 'ready' && "🥡 Hot and ready! Waiting for pickup."}
                    {order.delivery_status === 'picked_up' && "🛵 Your order has been picked up!"}
                    {order.delivery_status === 'in_transit' && "📍 On the way! See you in a bit."}
                    {order.status === 'delivered' && "✨ Delivered! Hope you love it! 🍽️"}
                </p>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .animate-shimmer {
                    animation: shimmer 2s infinite linear;
                }
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-6px); }
                }
                .animate-bounce-subtle {
                    animation: bounce-subtle 1.5s infinite ease-in-out;
                }
                @keyframes flow-line {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                .animate-flow-line {
                    background: linear-gradient(90deg, 
                        rgba(59, 130, 246, 0) 0%, 
                        rgba(59, 130, 246, 0.4) 50%, 
                        rgba(59, 130, 246, 0) 100%
                    );
                    background-size: 200% 100%;
                    animation: flow-line 2s infinite linear;
                }
                @keyframes pulse-slow {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.9; transform: scale(0.98); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 3s infinite ease-in-out;
                }
            `}} />
        </div>
    );
}
